import { useFragment, useQuery } from '@apollo/client';

import {
  GET_RD_PROJECT,
  GetRdProjectQuery,
  GetRdProjectQueryVariables,
  RdProjectFragmentDocument,
} from '@neo.tax/shared';

import { RdProject } from './useRdProjects';

export const useRdProject = (
  rdProjectId: string,
): {
  loading: boolean;
  rdProject: RdProject | null;
} => {
  // First, try to get the project from the cache using fragment
  const { complete, data: cachedData } = useFragment({
    fragment: RdProjectFragmentDocument,
    fragmentName: 'RdProject',
    from: {
      __typename: 'RdProject',
      id: rdProjectId,
    },
  });

  // Determine if we should skip the query
  const shouldSkip = complete && !!cachedData;

  // Only query if the data is not complete in the cache
  const {
    data: queryData,
    error,
    loading: queryLoading,
  } = useQuery<GetRdProjectQuery, GetRdProjectQueryVariables>(GET_RD_PROJECT, {
    variables: { rdProjectId },
    skip: shouldSkip,
    // Prevent auth errors from being thrown when project is deleted
    errorPolicy: 'all',
  });

  // Use cached data if available and complete, otherwise use query data
  const data = complete && cachedData ? cachedData : queryData?.getRdProject;

  // We're loading if the query is loading (and not skipped)
  const loading = !shouldSkip && queryLoading;

  // If there's an error and no data, return null instead of throwing
  // This handles cases where the project was deleted and we get an auth error
  if (error && !data) {
    return {
      loading: false,
      rdProject: null,
    };
  }

  // If there's an error but we have cached data, throw it so it can be caught by error boundaries
  if (error) {
    throw error;
  }

  if (!data) {
    return {
      loading,
      rdProject: null,
    };
  }

  return {
    loading,
    rdProject: data,
  };
};
